import { memo } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"
import SlickAIMessage from "@/components/chat/SlickAIMessage"
import DayMarker from "@/components/DayMarker"
import {
  areMessageFromDifferentDay,
  getCurrentMessageDate,
} from "@/lib/messageDate"
import type { MessageNeighbourType } from "@/components/chat/MessageRow"
import type { SlickAIGroup } from "@/reducers/messagesSlice"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"

/** Marks the group in the DOM so `ChatHistory` can anchor the scroll to it. */
export const SLICK_AI_GROUP_ATTRIBUTE = "data-slickai-group"

/**
 * A run of SlickAI automations, folded into one quiet line the dispatcher can
 * open when they want to. Aligned left, where the bubble would start — a line
 * across the whole chat reads as the end of a section and cuts the conversation
 * in two.
 *
 * The group is always ONE row, expanded or not: the bubbles render inside it.
 * See `collapseSlickAIGroups`.
 */
const SlickAIGroupRow = ({
  group,
  previousRow,
  onToggle,
}: {
  group: SlickAIGroup
  previousRow: MessageNeighbourType
  onToggle: () => void
}) => {
  const { t } = useTranslation()
  // Groups only ever render inside driver rooms, so the marker follows the
  // layout rather than a prop.
  const inWorkspace = useIsDriverWorkspace()
  // A group never spans midnight, so one marker covers the whole of it.
  const isDifferentDay =
    !!previousRow && areMessageFromDifferentDay(group, previousRow)

  return (
    <div {...{ [SLICK_AI_GROUP_ATTRIBUTE]: group.groupId }}>
      {isDifferentDay && (
        <DayMarker
          date={getCurrentMessageDate(group)}
          isDriverRoomChat={inWorkspace}
        />
      )}
      <button
        onClick={onToggle}
        className={cn(
          "ml-12 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-400 transition-colors",
          "hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-800",
          // expanded, the header is a caption over its group: it breathes above
          // and sits tight against the bubbles below
          group.expanded ? "mb-2 mt-4" : "my-3",
        )}
      >
        <Sparkles className="size-3 shrink-0" />
        <span>
          {group.expanded
            ? t("chat.slickAIGroup.hide")
            : t("chat.slickAIGroup.count", { count: group.messages.length })}
        </span>
        {group.expanded ? (
          <ChevronUp className="size-3 shrink-0" />
        ) : (
          <ChevronDown className="size-3 shrink-0" />
        )}
      </button>
      {group.expanded && (
        // Even spacing: inside the group we don't apply the 5-minute grouping,
        // so one avatar covers the whole run.
        <div className="flex flex-col gap-2">
          {group.messages.map((message, index) => (
            <SlickAIMessage
              key={message.id}
              message={message}
              grouped
              isLastInGrouped={index === group.messages.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default memo(SlickAIGroupRow)
